import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, externalLinkAttrs } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitStatus('submitting');
    setErrorMsg('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email.trim());

      const res = await fetch('https://readdy.ai/api/form/d8pvg0on4gr6p3b2g2n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      setSubmitStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  const col1 = categories.slice(0, 6);
  const col2 = categories.slice(6, 12);
  const col3 = categories.slice(12);

  return (
    <footer className="bg-background-100 border-t border-background-200/70">
      <div className="w-full px-6 md:px-10 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-[40%]">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
              Never Miss<br />A Drop
            </h3>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed max-w-sm">
              Subscribe for weekly Sugargoo spreadsheet updates, hidden finds, and exclusive curated lists delivered to your inbox.
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="mt-8 max-w-sm"
            >
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                Your Email
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full bg-transparent border-0 border-b border-foreground-300/40 pb-2 text-sm text-foreground-900 placeholder:text-foreground-400/60 focus:outline-none focus:border-accent-500 transition-colors"
              />

              {submitStatus === 'success' && (
                <p className="mt-2 text-xs text-accent-600 font-medium">You are on the list!</p>
              )}
              {submitStatus === 'error' && (
                <p className="mt-2 text-xs text-red-500">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="mt-5 px-6 py-2.5 rounded-md border border-foreground-300/40 text-sm font-medium text-foreground-800 hover:border-foreground-500 hover:text-foreground-950 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {submitStatus === 'submitting' ? 'Joining...' : 'Join The List'}
              </button>
            </form>
          </div>

          <div className="lg:w-[60%] grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
                Categories
              </h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
                    Home
                  </Link>
                </li>
                {col1.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={cat.url}
                      {...externalLinkAttrs}
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
                More Categories
              </h4>
              <ul className="space-y-2.5">
                {col2.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={cat.url}
                      {...externalLinkAttrs}
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
                Resources
              </h4>
              <ul className="space-y-2.5">
                {col3.map((cat) => (
                  <li key={cat.id}>
                    <a
                      href={cat.url}
                      {...externalLinkAttrs}
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
                <li>
                  <Link to="/article/best-sugargoo-spreadsheet-2026" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
                    Complete Guide
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com/"
                    {...externalLinkAttrs}
                    className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap"
                  >
                    Main Website
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-background-200/60 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">
            &copy; 2026 SugarGooSpreadsheet.team. All external links open in new tabs. This site provides informational shopping guides only.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors whitespace-nowrap">
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              {...externalLinkAttrs}
              className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors whitespace-nowrap"
            >
              Visit Main Site
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}